(() => {
  // --------- DOM targets (desktop/tablet only) ----------
  const inputEl = document.getElementById('predictive-search-input'); // md+
  const panel   = document.getElementById('predictive-panel');
  if (!panel || !inputEl) return;

  // --------- constants ----------
  const BP_XL = 1280;     // desktop ≥1280
  const GAP = 12;
  const DESKTOP_W = 890;
  const TABLET_MAX_W = 860;
  const VIEW_GUTTER = 16;
  const CARET_LEFT_DESKTOP = 64;
  const CARET_INSET = 24;
  const CARET_RIGHT_PAD = 40;

  const isDesktop = () => window.innerWidth >= BP_XL;
  const isTablet  = () => window.innerWidth < BP_XL;
  const mode = () => (isDesktop() ? 'desktop' : 'tablet');

  // ---------- helpers ----------
  function debounce(fn, ms = 200) {
    let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); };
  }
  const escapeHtml = (s='') =>
    s.replace(/[&<>"']/g, m => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[m]));

  // Symbol-only money (e.g. "$22.38", "€21.95") — no ISO currency codes.
  function money(cents) {
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'CAD';
    const locale = (navigator.language || 'en-CA').replace('_', '-');
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency, currencyDisplay: 'narrowSymbol' }).format((cents || 0) / 100);
    } catch (e) {
      return ((cents || 0) / 100).toFixed(2);
    }
  }

  function renderPriceFromPredictive(p) {
    // Predictive results may expose different keys depending on Shopify/theme
    const price =
      p.price != null ? p.price :
      p.price_min != null ? p.price_min :
      p?.variants?.[0]?.price;

    const compare =
      p.compare_at_price != null ? p.compare_at_price :
      p.compare_at_price_min != null ? p.compare_at_price_min :
      p?.variants?.[0]?.compare_at_price;

    const priceHtml = price != null ? moneyFromPredictive(price) : '';
    const compareHtml = compare != null ? moneyFromPredictive(compare) : '';

    const onSale =
      price != null &&
      compare != null &&
      Number(compare) > Number(price);

    // Output matches your style (light, clean)
    return onSale
      ? `
        <span class="text-[14px] font-light text-[var(--color-brand-strong)]">${priceHtml}</span>
        <span class="ml-2 text-[14px] font-light text-zinc-400 line-through">${compareHtml}</span>
      `
      : `<span class="text-[14px] font-light text-zinc-500">${priceHtml}</span>`;
  }


  // Predictive API exposes price in major units (dollars); money() takes cents.
  function moneyFromPredictive(price) {
    const amount = Number(price || 0);
    if (Number.isNaN(amount)) return '';
    return money(Math.round(amount * 100));
  }

  // ---------- caret ----------
  function positionCaret() {
    if (panel.classList.contains('hidden')) return;

    if (isDesktop()) {
      panel.style.setProperty('--caret-left', `${CARET_LEFT_DESKTOP}px`);
      return;
    }

    // tablet: caret aligns near the right of the input, within panel bounds
    const pr = panel.getBoundingClientRect();
    const ir = inputEl.getBoundingClientRect();
    let x = Math.round(ir.right - pr.left - (CARET_INSET + CARET_RIGHT_PAD));
    const MIN = 20, MAX = pr.width - 20;
    x = Math.max(MIN, Math.min(MAX, x));
    panel.style.setProperty('--caret-left', `${x}px`);
  }

  // ---------- layout ----------
  function layoutPanel() {
    if (panel.classList.contains('hidden')) return;

    const ir = inputEl.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;

    if (isDesktop()) {
      const top = Math.round(ir.bottom + GAP);
      const width = Math.min(DESKTOP_W, vw - VIEW_GUTTER * 2);
      let left = Math.round(ir.left);
      left = Math.max(VIEW_GUTTER, Math.min(left, vw - width - VIEW_GUTTER));

      Object.assign(panel.style, {
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        width: `${width}px`,
        maxWidth: `${width}px`,
        transform: 'none',
        right: 'auto',
        margin: '0',
        boxSizing: 'border-box',
        zIndex: '2147483647',
        overflow: 'visible'
      });
    } else {
      // TABLET
      const top = Math.round(ir.bottom + GAP);
      const width = Math.min(Math.round(vw * 0.94), TABLET_MAX_W);

      Object.assign(panel.style, {
        position: 'fixed',
        top: `${top}px`,
        left: '50%',
        transform: 'translateX(-50%)',
        width: `${width}px`,
        maxWidth: `${width}px`,
        right: 'auto',
        margin: '0',
        boxSizing: 'border-box',
        zIndex: '2147483647',
        overflow: 'visible'
      });
    }

    positionCaret();
  }

  // ---------- open/close ----------
  function openPanel(){
    panel.classList.remove('hidden');
    inputEl.setAttribute('aria-expanded','true');
    requestAnimationFrame(layoutPanel);
  }

  function closePanel(){
    panel.classList.add('hidden');
    inputEl.setAttribute('aria-expanded','false');
    panel.innerHTML = '';
    activeIndex = -1;
  }

  // ---------- rendering ----------
  function itemProduct(p) {
    const img = p.image ? `${p.image}?width=160` : '';
    const price = p.price != null ? p.price : p?.variants?.[0]?.price;
    return `
      <a role="option" tabindex="-1" href="${p.url}"
         class="group flex items-center gap-3 px-2 py-2">
        ${ img
          ? `<img src="${img}" alt="${escapeHtml(p.title)}"
                  class="h-16 w-16 object-cover flex-none" loading="lazy">`
          : `<div class="h-16 w-16 bg-black/5 flex-none"></div>` }
        <div class="min-w-0">
          <div class="text-[15px] text-zinc-700 font-light leading-tight line-clamp-2">
            ${escapeHtml(p.title)}
          </div>
          <div class="mt-1.5 flex items-baseline flex-wrap">
            ${renderPriceFromPredictive(p)}
          </div>
        </div>
      </a>`;
  }

  const itemQuery = (qi) => {
    const label = qi.title || qi.text || '';
    return `
      <a role="option" tabindex="-1" href="${qi.url}"
        class="block text-[13px] text-zinc-700 hover:bg-[var(--color-accent)] focus:bg-[var(--color-accent)]
                w-[calc(100%+1rem)] -mx-2 px-4 py-2 transition-colors">
        ${escapeHtml(label)}
      </a>`;
  };

  const itemCollection = (c) =>
    `<a role="option" tabindex="-1" href="${c.url}"
      class="block text-[13px] text-zinc-700 hover:bg-[var(--color-accent)] focus:bg-[var(--color-accent)]
              w-[calc(100%+1rem)] -mx-2 px-4 py-2 transition-colors">
      ${escapeHtml(c.title)}
    </a>`;

  // ---------- networking ----------
  const LIMITS = { product: 6, query: 6, collection: 4 };
  const base = (window.routes && window.routes.predictive_search_url) || '/search/suggest.json';
  const qs = (q) => new URLSearchParams({
    q,
    'resources[type]': 'product,collection,query',
    'resources[limit]': 10,          // Shopify will return up to 10 of each
    'resources[limit_scope]': 'each' // applied separately to products, collections, queries
  });


  // helper: when there are no products for the query,
  // fetch products for the FIRST suggestion (did you mean)
  async function handleDidYouMean(q, suggs) {
    const corrected = (suggs[0]?.title || suggs[0]?.text || '').trim();
    if (!corrected || corrected.toLowerCase() === q.toLowerCase()) return null;

    const url = `${base}?${qs(corrected)}`;
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) return null;

    const data = await res.json();
    return {
      corrected,
      data
    };
  }

  function render(data, q) {
    // if did-you-mean was used, override product list
    const prods = data.didYouMeanResults
    ? data.didYouMeanResults
    : (data?.resources?.results?.products ?? []).slice(0, LIMITS.product);
    const suggs = (data?.resources?.results?.queries ?? []).slice(0, LIMITS.query);
    const colls = (data?.resources?.results?.collections ?? []).slice(0, LIMITS.collection);
    const hasQ = !!q;
    const didYouMean = data.didYouMean || null;

    const emptyBlock = `
      <div class="col-span-2 px-4 py-3 text-[13px] text-zinc-700">
        <div>
          Sorry, nothing found for
          <span class="font-bold">'${escapeHtml(q)}'</span>.
        </div>
      </div>
    `;

    const didYouMeanBlock = didYouMean
      ? `
        <div class="col-span-2 px-4 py-3 text-[13px] text-zinc-700">
          <div>
            Sorry, nothing found for
            <span class="font-bold">'${escapeHtml(q)}'</span>.
          </div>
          <div class="mt-1 text-[13px] text-zinc-500">
            Did you mean
            <a href="${(window.routes?.search_url || '/search')}?q=${encodeURIComponent(didYouMean)}"
               class="ml-1 underline underline-offset-2 decoration-zinc-400 hover:decoration-zinc-600 text-zinc-700">
              ${escapeHtml(didYouMean)}
            </a>?
          </div>
        </div>
      `
      : '';

    const footerCTA = hasQ
      ? `<a href="${(window.routes?.search_url || '/search')}?q=${encodeURIComponent(q)}"
            class="ps-cta text-zinc-700 text-[13px]">
           View all results for <b>${escapeHtml(q)}</b>
         </a>`
      : '';

    // TABLET/DESKTOP ONLY
    panel.innerHTML = `
      <span class="ps-caret" aria-hidden="true"></span>
      <div class="scroll-area max-h-96 md:max-h-[32rem] overflow-auto overscroll-contain">
        <div class="md:flex rounded-none">
          <div class="w-full md:w-[65%]">
            <div class="relative px-4 pt-3 pb-2 text-[11px] font-medium uppercase tracking-wide text-zinc-400">
              Product matches
              <div class="absolute bottom-0 left-4 right-4 h-px bg-zinc-200/40"></div>
            </div>
            <div class="px-2 pb-3 grid grid-cols-2 gap-x-4 gap-y-3">
              ${
                didYouMean
                  ? didYouMeanBlock
                  : (!prods.length ? emptyBlock : '')
              }
              ${ prods.length ? prods.map(itemProduct).join('') : '' }
            </div>
          </div>
          <div class="hidden md:flex md:w-[35%] md:flex-col md:border-l md:border-zinc-200/40 bg-white/90 backdrop-blur-md">
            <div class="relative px-4 pt-3 pb-2 mb-2 text-[11px] font-medium uppercase tracking-wide text-zinc-400">
              Search suggestions
              <div class="absolute bottom-0 left-4 right-4 h-px bg-zinc-200/40"></div>
            </div>
            <div class="px-2 pb-3 flex flex-col gap-0.5">
              ${ suggs.length ? suggs.map(itemQuery).join('') : `<div class="px-2 py-2 text-[13px] text-zinc-700">No suggestions</div>` }
            </div>
            ${
              colls.length
                ? `
                  <div class="relative px-4 pt-2 pb-2 mb-2 text-[11px] font-medium uppercase tracking-wide text-zinc-400 border-t border-zinc-200/40 bg-white/90 backdrop-blur-md">
                    Collections
                    <div class="absolute bottom-0 left-4 right-4 h-px bg-zinc-200/40"></div>
                  </div>
                  <div class="px-2 pb-3 flex flex-col gap-0.5">
                    ${ colls.map(itemCollection).join('') }
                  </div>`
                : ``
            }
          </div>
        </div>
      </div>
      ${ footerCTA ? `<div class="border-t border-zinc-200/40 backdrop-blur-md px-4 py-2 text-center bg-white/60">${footerCTA}</div>` : ``}
    `;

    activeIndex = -1;
    requestAnimationFrame(layoutPanel);
  }

  let currentAbort = null;
  async function fetchAndRender(val){
    if (currentAbort) currentAbort.abort();
    currentAbort = new AbortController();
    try{
      const res = await fetch(`${base}?${qs(val)}`, { headers: { Accept: 'application/json' }, signal: currentAbort.signal });
      if (!res.ok) return;
      const data = await res.json();

      const products = data?.resources?.results?.products ?? [];
      const suggs = data?.resources?.results?.queries ?? [];

      // if no products but we have suggestions → "Did you mean"
      if (!products.length && suggs.length) {
        const didYouMeanData = await handleDidYouMean(val, suggs);
        if (didYouMeanData) {
          render({
            ...data,
            didYouMean: didYouMeanData.corrected,
            didYouMeanResults: didYouMeanData.data.resources.results.products ?? []
          }, val);
          openPanel();
          return;
        }
      }

      render(data, val);
      openPanel();
    } catch {} finally {
      currentAbort = null;
    }
  }

  // ---------- input wiring ----------
  let composing = false;
  const wireInput = (el) => {
    if (!el) return;
    el.addEventListener('compositionstart', () => { composing = true; });
    el.addEventListener('compositionend',   () => { composing = false; el.dispatchEvent(new Event('input')); });

    el.addEventListener('input', debounce(async (e) => {
      if (composing) return;
      const q = e.target.value.trim();

      if (!q) {
        closePanel();
        return;
      }
      await fetchAndRender(q);
    }, 200));

    el.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { closePanel(); el.blur(); }
      else if (e.key === 'ArrowDown') { if (!panel.classList.contains('hidden')) { e.preventDefault(); moveFocus(+1); } }
      else if (e.key === 'ArrowUp')   { if (!panel.classList.contains('hidden')) { e.preventDefault(); moveFocus(-1); } }
    });

    el.addEventListener('focus', () => { if (panel.innerHTML.trim()) openPanel(); });
  };

  wireInput(inputEl);

  // ---------- keyboard focus cycling inside panel ----------
  let activeIndex = -1;
  const focusables = () => panel.querySelectorAll('a[role="option"]');
  function moveFocus(dir){
    const items = focusables();
    if (!items.length) return;
    activeIndex = (activeIndex + dir + items.length) % items.length;
    items[activeIndex].focus();
  }

  panel.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { closePanel(); inputEl?.focus?.(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); moveFocus(+1); }
    else if (e.key === 'ArrowUp')   { e.preventDefault(); moveFocus(-1); }
  });

  // ---------- breakpoint reset on resize/orientation (tablet <-> desktop) ----------
  let lastMode = mode();
  const onResize = debounce(() => {
    const now = mode();
    if (now !== lastMode) {
      // hard reset when crossing tablet/desktop boundary
      if (currentAbort) { try { currentAbort.abort(); } catch {} }
      closePanel();
      activeIndex = -1;
      lastMode = now;
      return; // do NOT auto-reopen; user must type again
    }
    // same mode: keep layout in sync if panel is open
    if (!panel.classList.contains('hidden')) layoutPanel();
  }, 120);

  window.addEventListener('resize', onResize, { passive: true });
  window.addEventListener('orientationchange', onResize, { passive: true });
  window.addEventListener('scroll', () => { if (!panel.classList.contains('hidden')) layoutPanel(); }, { passive: true, capture: true });

  // ---------- close when clicking outside ----------
  document.addEventListener('click', (e) => {
    // if panel is hidden, do nothing
    if (panel.classList.contains('hidden')) return;

    const target = e.target;
    const clickedInsideInput = inputEl.contains(target);
    const clickedInsidePanel = panel.contains(target);

    if (!clickedInsideInput && !clickedInsidePanel) {
      closePanel();
    }
  });

  document.addEventListener('touchstart', (e) => {
    if (panel.classList.contains('hidden')) return;
    const target = e.target;
    if (!inputEl.contains(target) && !panel.contains(target)) {
      closePanel();
    }
  }, { passive: true });
})();
